import React, { useRef } from 'react';
import { Paper, CircularProgress, Snackbar, SnackbarContent } from '@material-ui/core';
import translate from '../../locales';
import { useEffect, useState } from 'react';
import { styleWithCommonClasses } from '../../_constants/emparkConstants';

import TitleToolbar from '../Forms/TitleToolbar';
import { useExecution } from '../../hooks/useExecution';
import { createParameter, deleteParameter, getParameters, updateParameter } from '../../services/parametersService';
import ListItem from '../ListItem';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import LoadingDialog from '../Dialogs/LoadingDialog';
import ParameterChildContainer from './ParameterChildContainer';
import ParameterForm from '../Forms/Parameters/ParameterForm';
import { positionsService } from '../../services';
import ParameterPreview from '../Parameters/ParameterPreview';
import { contextsActions } from '../../actions/contextsActions';
import { connect } from 'react-redux';

const __t = translate;

const styles = styleWithCommonClasses({
    paddingOnTop: {
        paddingTop: '20px'
    },
    upperCase: {
        textTransform: 'uppercase'
    },
    containerSelect: {
        width: '100%',
        padding: '4px'
    },
    errorBorder: {
        border: '1px solid red',
        borderRadius: '5px'
    },
})

const ParameterContainerController = (props) => {
    const [parameters, setParameters] = useState([]);
    const [positions, setPositions] = useState([]);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState({ text: '', isError: false });

    const fetchParametersQuery = useExecution(getParameters);
    const fetchPositionsQuery = useExecution(positionsService.getPositions);

    useEffect(() => {
        fetchParametersQuery.execute(props.contextId)
            .then(data => {
                setParameters(data);
            })
            .catch(err => {
                console.error('Error fetching parameters:', err);
                setError('Unexpected Error');
            });

        fetchPositionsQuery.execute(props.contextId)
            .then(data => {
                setPositions(data);
            })
            .catch(err => {
                console.error('Error fetching positions:', err);
                setError('Unexpected Error');
            });
    }, [props.contextId]);

    if (fetchParametersQuery.loading || fetchPositionsQuery.loading) {
        return (
            <Paper style={{ padding: '20px', textAlign: 'center' }}>
                <CircularProgress color='secondary' />
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper style={{ padding: '20px', textAlign: 'center' }}>
                <div>{error}</div>
            </Paper>
        );
    }

    return (
        <>
            <ParameterPage
                contextId={props.contextId}
                allPositions={positions}
                parameters={parameters}
                setParameters={setParameters}
                setMessage={setMessage}
                onStreet={props.onStreet}
                setContextAsDraft={props.setContextAsDraft}
            />
            <Snackbar
                open={!!(message.text)}
                autoHideDuration={5000}
                onClose={() => setMessage({ text: '', isError: message.isError })}
            >
                <SnackbarContent style={{ color: 'white', backgroundColor: message.isError ? 'red' : 'green' }} message={message.text} />
            </Snackbar>
        </>
    )
}

const ParameterPage = (props) => {
    const [mode, setMode] = useState({
        type: 'table', // table | view | add | edit
        parameter: null
    });

    const [deleteModal, setDeleteModal] = useState(null);

    const createParameterQuery = useExecution(createParameter);
    const updateParameterQuery = useExecution(updateParameter);
    const deleteParameterQuery = useExecution(deleteParameter);

    const formRef = useRef(null);

    if (mode.type === 'table') {
        return (
            <Paper style={{ marginTop: '10px', padding: '15px' }}>
                <LoadingDialog loading={deleteParameterQuery.loading} />
                <ConfirmDialog
                    open={deleteModal != null && !deleteParameterQuery.loading}
                    title={__t('confirm_delete')}
                    onClose={(action) => {
                        if (action != 'ok') {
                            setDeleteModal(null);
                            return;
                        }

                        deleteParameterQuery.execute(props.contextId, deleteModal.id)
                            .then(() => {
                                props.setParameters(props.parameters.filter(p => p.id !== deleteModal.id));
                                setDeleteModal(null);

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                    }}
                />
                <TitleToolbar
                    title={__t('parameters')}
                    onAdd={() => {
                        setMode({ type: 'add', parameter: null });
                    }}
                >
                    {props.parameters.map(p =>
                        <ListItem
                            key={p.id}
                            onView={() => {
                                setMode({ type: 'view', parameter: p });
                            }}
                            onDelete={() => {
                                setDeleteModal(p);
                            }}
                        >
                            <ParameterPreview
                                name={p.name}
                                positions={p.positions.map(pos => pos.name).join(', ')}
                                groups={p.groups.join(', ')}
                                emissionProfiles={p.emissionProfiles.join(', ')}
                                showEmissionProfiles={props.onStreet}
                            />
                        </ListItem>
                    )}
                </TitleToolbar>
            </Paper>
        )
    }

    return (
        <Paper style={{ marginTop: '10px', padding: '15px' }}>
            <LoadingDialog loading={createParameterQuery.loading || updateParameterQuery.loading} />
            <TitleToolbar
                title={`${__t(mode.type)} ${__t('parameters')}`}
                onBack={mode.type == 'view' && (() => {
                    setMode({ type: 'table', parameter: null });
                })}
                onEdit={mode.type == 'view' && (() => {
                    setMode({ type: 'edit', parameter: mode.parameter });
                })}
                onSave={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    const formData = formRef.current.getData();
                    if (!formData) {
                        return;
                    }

                    const payload = {
                        name: formData.name,
                        positionIds: formData.positions.map(p => p.id),
                        groups: formData.groups,
                        emissionProfiles: formData.emissionProfiles
                    }

                    if (mode.type === 'add') {
                        createParameterQuery.execute(props.contextId, payload)
                            .then((newParameter) => {
                                props.setParameters([...props.parameters, newParameter]);
                                setMode({ type: 'view', parameter: newParameter });

                                props.setContextAsDraft();
                            })
                            .catch(err => {
                                props.setMessage({ text: err || 'Unexpected error', isError: true });
                            });
                        return;
                    }

                    updateParameterQuery.execute(props.contextId, mode.parameter.id, payload)
                        .then((newParameter) => {
                            props.setParameters(props.parameters.map(p => p.id === mode.parameter.id ? newParameter : p));
                            setMode({ type: 'view', parameter: newParameter });

                            props.setContextAsDraft();
                        })
                        .catch(err => {
                            props.setMessage({ text: err || 'Unexpected error', isError: true });
                        });

                })}
                onCancel={(mode.type == 'edit' || mode.type == 'add') && (() => {
                    if (mode.type === 'add') {
                        setMode({ type: 'table', parameter: null });
                        return;
                    }

                    setMode({ type: 'view', parameter: mode.parameter });
                })}
            >
                <ParameterForm
                    key={'form-parameter-' + mode.type}
                    ref={formRef}
                    allPositions={props.allPositions}
                    parameter={mode.parameter}
                    showEmissionProfiles={props.onStreet}
                    readonly={mode.type === 'view'}
                />

                <div style={{ marginTop: '20px' }}></div>

                {mode.type == 'view' &&
                    <ParameterChildContainer
                        contextId={props.contextId}
                        parameterId={mode.parameter.id}
                        setMessage={props.setMessage}
                        showRules={props.onStreet}
                    />
                }
            </TitleToolbar>
        </Paper >
    )
}

export default connect(
    () => ({}),
    (dispatch) => ({
        setContextAsDraft: () => {
            dispatch(contextsActions.setAsDraft());
        }
    }))(ParameterContainerController);
